import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { C1Option, C1Subtitle, C1Title } from '@/components/c1';
import { useAppContext } from '@/hooks';

interface ContextSwitcherProps {
  open: boolean;
  onClose: () => void;
}

export function ContextSwitcher({ open, onClose }: ContextSwitcherProps) {
  const { t } = useTranslation();
  const { activeContext, availableContexts, setActiveContext } = useAppContext();
  const [switching, setSwitching] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const roleColors: Record<string, string> = {
    admin: 'var(--c1-blue)',
    teacher: '#0a7b3c',
    student: '#7b4f0a',
    parent: '#6b3fa0',
  };

  const handleSelect = async (id: string) => {
    setSwitching(true);
    try {
      await setActiveContext(id);
      onClose();
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} role="presentation" />
      <div
        ref={panelRef}
        className="context-switcher"
        role="dialog"
        aria-modal="true"
        aria-label={t('context.title')}
        tabIndex={-1}
      >
        <div className="context-switcherHeader">
          <C1Title as="h2">{t('context.title')}</C1Title>
          <C1Subtitle>{t('context.subtitle')}</C1Subtitle>
          <button className="context-switcherClose" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className="context-switcherList" role="radiogroup" aria-label={t('context.title')}>
          {availableContexts.length === 0 ? (
            <div className="context-empty">
              <Icon name="noAccess" size={36} aria-hidden="true" />
              <p>{t('context.noContexts')}</p>
            </div>
          ) : (
            availableContexts.map((ctx) => {
              const isActive = activeContext?.id === ctx.id;
              // A group context has no institution — its seat name is the group's.
              const seatName = ctx.institutionName ?? ctx.groupName;
              return (
                <C1Option
                  key={ctx.id}
                  title={ctx.roleName}
                  description={seatName}
                  media={
                    <span
                      className="context-cardDot"
                      style={{ background: roleColors[ctx.roleCode] || 'var(--c1-ink-muted)' }}
                    />
                  }
                  selected={isActive}
                  disabled={switching}
                  onSelect={() => handleSelect(ctx.id)}
                  aria-label={`${ctx.roleName} at ${seatName}`}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
